/**
 * Analytics bridge: the app never talks to any analytics SDK directly.
 * It posts a small, whitelisted set of events to the parent window (Webflow)
 * via postMessage, and Webflow's own embed script is responsible for
 * forwarding them into window.dataLayer. This keeps the iframe's origin
 * boundary intact and makes the "no PII" rule trivially auditable: it's
 * enforced once, here, not scattered across every call site.
 */

export type AnalyticsEvent =
  | "product_calculator_view"
  | "product_calculator_scope"
  | "product_calculator_contact"
  | "product_calculator_result"
  | "whatsapp_quote_ready";

/** Keys that must never appear in an analytics payload, enforced at runtime. */
const FORBIDDEN_KEYS = new Set(["nombre", "name", "telefono", "phone", "whatsapp", "email", "empresa", "company", "texto", "text", "message", "mensaje"]);

function assertNoPII(params: Record<string, unknown>) {
  for (const key of Object.keys(params)) {
    if (FORBIDDEN_KEYS.has(key.toLowerCase())) {
      throw new Error(`analytics: refusing to send potentially-PII key "${key}"`);
    }
  }
}

let allowedParentOrigins: string[] = [];
try {
  const raw = (import.meta as any).env?.VITE_ALLOWED_PARENT_ORIGINS as string | undefined;
  allowedParentOrigins = (raw || "").split(",").map((s) => s.trim()).filter(Boolean);
} catch {
  allowedParentOrigins = [];
}

function resolveTargetOrigin(): string | null {
  if (window.parent === window) return null; // not embedded, nothing to post to
  // We don't have direct access to the parent's origin from the iframe when
  // cross-origin; we send to each allow-listed origin explicitly instead of
  // using "*", so a compromised/unexpected parent never receives our events.
  return allowedParentOrigins.length > 0 ? allowedParentOrigins[0] : null;
}

export function trackEvent(event: AnalyticsEvent, params: Record<string, unknown> = {}): void {
  assertNoPII(params);
  const payload = { source: "producto-estimador", event, params };

  if (allowedParentOrigins.length === 0) {
    // Standalone mode (no parent, or no allow-list configured): no-op.
    // We intentionally do not fall back to "*" or to window.location.origin.
    return;
  }

  for (const origin of allowedParentOrigins) {
    try {
      window.parent.postMessage(payload, origin);
    } catch {
      /* fail-safe: analytics must never break the app */
    }
  }
}

/**
 * Validates an incoming postMessage's origin against the same allow-list,
 * for any future two-way communication (e.g. the parent asking the app to
 * reset). Not wired to anything yet — provided so the boundary is defined
 * in one place before it's needed.
 */
export function isTrustedOrigin(origin: string): boolean {
  return allowedParentOrigins.includes(origin);
}

/**
 * Reports the app's real content height to the parent so it can resize the
 * iframe and avoid a nested scrollbar. Separate from trackEvent: this is
 * layout plumbing, not an analytics event, and carries no params to audit.
 */
export function reportHeight(px: number): void {
  if (allowedParentOrigins.length === 0) return;
  const payload = { source: "producto-estimador-resize", height: Math.ceil(px) };
  for (const origin of allowedParentOrigins) {
    try {
      window.parent.postMessage(payload, origin);
    } catch {
      /* fail-safe: resize reporting must never break the app */
    }
  }
}
